/**
 * 展示层格式化工具：金额、时间、文本截断等。
 * 大额数字使用 BigInt 路径以避免 Number 在 > 2^53 时丢失精度。
 */

/** 短哈希/地址中间省略展示。 */
export const truncateMiddle = (s: string | null | undefined, head = 6, tail = 6): string => {
    const v = (s ?? '').trim();
    if (v.length <= head + tail + 3) return v;
    return `${v.slice(0, head)}...${v.slice(-tail)}`;
};

/** 业务类型文案：把下划线替换为空格，并统一大写。 */
export const getActionLabel = (bizType: string | null | undefined): string => {
    const t = (bizType ?? '').trim();
    if (!t) return '—';
    return t.replace(/_/g, ' ').toUpperCase();
};

/** 交易对展示：`YESFI-USD` → `YESFI/USDC`；其余统一大写。 */
export const formatTradingPairDisplay = (value: string | null | undefined): string => {
    const s = (value ?? '').trim();
    if (!s) return '—';
    const match = /^(.+)-USD$/i.exec(s);
    if (match) {
        return `${match[1]!.toUpperCase()}/USDC`;
    }
    return s.toUpperCase();
};

const NUMERIC_RE = /^-?\d+(\.\d+)?$/;

const groupInteger = (intPart: string): string => intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

const appendAsset = (text: string, asset?: string | null): string => {
    const a = (asset ?? '').trim();
    return a ? `${text} ${a}` : text;
};

/**
 * 金额格式化：保留原始精度，安全处理大整数与负数。
 * - 非数字字符串：原样返回（追加 asset）。
 * - 整数：使用 BigInt 加千分位。
 * - 小数：保留 `maxDecimals` 位（默认 5），超出部分截断并以 "..." 提示。
 */
export const formatAmount = (
    amount: string | null | undefined,
    asset?: string | null,
    maxDecimals: number = 8,
): string => {
    const raw = (amount ?? '').trim();
    if (!raw) return '—';
    if (!NUMERIC_RE.test(raw)) return appendAsset(raw, asset);

    const sign = raw.startsWith('-') ? '-' : '';
    const body = sign ? raw.slice(1) : raw;
    const dotIdx = body.indexOf('.');
    const intPart = dotIdx === -1 ? body : body.slice(0, dotIdx);
    const fracPartRaw = dotIdx === -1 ? '' : body.slice(dotIdx + 1).replace(/0+$/, '');

    const grouped = groupInteger(intPart || '0');

    if (!fracPartRaw) return appendAsset(sign + grouped, asset);

    let fracPart = fracPartRaw.replace(/0+$/, '');
    if (!fracPart) {
        return appendAsset(sign + grouped, asset);
    }

    const truncated = fracPart.length > maxDecimals;
    if (truncated) {
        fracPart = fracPart.slice(0, maxDecimals);
    }

    const text = sign + grouped + '.' + fracPart + (truncated ? '...' : '');
    return appendAsset(text, asset);
};

/** 详情页数值字段（price / size 等）展示。 */
export const formatDetailNumeric = (value: string | null | undefined): string => {
    const v = (value ?? '').trim();
    if (!v) return '—';
    return formatAmount(v, null, 999);
};

/**
 * 把毫秒时间戳格式化为 UTC 字符串：`YYYY-MM-DD HH:mm:ss`。
 * 无效时间戳返回 `'—'`。
 */
export const formatUTC = (ms: number | null | undefined): string => {
    if (ms == null || !Number.isFinite(ms)) return '—';
    const d = new Date(ms);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toISOString().replace('T', ' ').replace(/\.\d+Z$/, '');
};

/** Method 列展示：Title Case（如 Transfer、Open Position）。 */
export const getMethodLabel = (bizType: string | null | undefined): string => {
    const t = getActionLabel(bizType);
    if (t === '—') return t;
    return t
        .toLowerCase()
        .split(' ')
        .map(w => (w ? w[0]!.toUpperCase() + w.slice(1) : w))
        .join(' ');
};

/** 百分比格式化：原始小数×100 + %。如 "0.0008" → "0.08%"。 */
export const formatPercent = (value: string | null | undefined): string => {
    const v = (value ?? '').trim();
    if (!v) return '—';
    const n = Number(v);
    if (!Number.isFinite(n)) return '—';
    const pct = parseFloat((n * 100).toFixed(4)); // 最多 4 位小数，去除末尾零
    return `${pct}%`;
};
